import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, Video, MessageSquare, Zap, Phone, Calendar,
  Sparkles, BookOpen, ShoppingBag, ClipboardList, Clock, ArrowRight,
} from "lucide-react";
import { KD9Watermark } from "@/components/KD9Logo";
import api from "@/lib/api";

/* User Dashboard — Acompañamiento humano on TOP */
const UserDashboard = () => {
  const navigate = useNavigate();
  const [roleRequests, setRoleRequests] = useState({ consultant: null, creator: null });

  useEffect(() => {
    let cancelled = false;
    api.get("/api/role-requests/mine")
      .then(({ data }) => {
        if (cancelled) return;
        const latest = { consultant: null, creator: null };
        for (const request of Array.isArray(data) ? data : []) {
          const kind = request?.request_type;
          if (!(kind in latest)) continue;
          if (!latest[kind] || new Date(request.created_at) > new Date(latest[kind].created_at)) {
            latest[kind] = request;
          }
        }
        setRoleRequests(latest);
      })
      .catch(() => {
        // Role-request status is informative only; dashboard access must never
        // be blocked if this auxiliary request is temporarily unavailable.
      });
    return () => { cancelled = true; };
  }, []);

  const pendingRoles = [];
  if (roleRequests.consultant?.status === "pending") pendingRoles.push({ kind: "consultor", label: "Consultor KD9", color: "#A855F7" });
  if (roleRequests.creator?.status === "pending") pendingRoles.push({ kind: "creador", label: "Creador Marketplace", color: "#A67C00" });
  if (roleRequests.consultant?.status === "rejected") pendingRoles.push({ kind: "consultor", label: "Consultor KD9", color: "#7A0E17", rejected: true });
  if (roleRequests.creator?.status === "rejected") pendingRoles.push({ kind: "creador", label: "Creador Marketplace", color: "#7A0E17", rejected: true });

  const COMPANION = [
    { title: "Agendar videollamada", desc: "Videollamada KD9 con duración, valor y pago propios", icon: Video, color: "#3C78B5", to: "/app/videollamadas-kd9" },
    { title: "Solicitar acompañamiento", desc: "Flujo exclusivo de acompañamientos", icon: Heart, color: "#1FA774", to: "/app/acompanamiento" },
    { title: "Hablar con un consultor", desc: "Conversación privada, separada de soporte", icon: MessageSquare, color: "#A67C00", to: "/app/hablar-consultor" },
    { title: "Consulta rápida", desc: "Servicio breve configurado por Dirección", icon: Zap, color: "#7A0E17", to: "/app/consulta-rapida" },
  ];

  const QUICK_ACCESS = [
    { title: "Solicitar diagnóstico", icon: Sparkles, to: "/app/diagnosticos", color: "#D4AF37" },
    { title: "Mis diagnósticos", icon: ClipboardList, to: "/app/mis-diagnosticos", color: "#A855F7" },
    { title: "Academia KD9", icon: BookOpen, to: "/app/academia", color: "#3C78B5" },
    { title: "Marketplace", icon: ShoppingBag, to: "/app/tienda", color: "#A67C00" },
    { title: "Agenda", icon: Calendar, to: "/app/agenda", color: "#1FA774" },
  ];

  return (
    <div className="relative" data-testid="user-dashboard">
      <KD9Watermark size={520} className="-right-20 top-20" />

      {/* PENDING / REJECTED ROLE REQUESTS */}
      {pendingRoles.length > 0 && (
        <section className="kd9-card p-6 mb-6" data-testid="user-pending-requests">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} style={{ color: "var(--kd9-gold)" }} />
            <h3 className="font-cinzel kd9-gold-text text-lg tracking-widest">
              SOLICITUDES EN REVISIÓN
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {pendingRoles.map((p) => (
              <div
                key={p.kind}
                className="flex items-center gap-4 p-4 rounded-xl border"
                style={{ borderColor: `${p.color}55`, background: `${p.color}10` }}
                data-testid={`pending-${p.kind}`}
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `${p.color}22`, border: `1px solid ${p.color}55` }}
                >
                  <Clock size={18} style={{ color: p.color }} />
                </div>
                <div className="flex-1">
                  <div className="font-mont font-semibold text-sm">{p.label}</div>
                  <div className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
                    Estado: <span style={{ color: p.color }}>{p.rejected ? "Solicitud rechazada — puedes reenviarla" : "Pendiente de aprobación"}</span>
                  </div>
                </div>
                <Link to="/app/mis-solicitudes" className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] flex items-center gap-1" data-testid={`pending-cta-${p.kind}`}>
                  Ver estado <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ACOMPAÑAMIENTO — PRIORITY 1 */}
      <section
        className="relative kd9-card overflow-hidden p-8 lg:p-10 mb-8 kd9-glow"
        data-testid="user-acompanamiento-priority"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 80% 30%, rgba(31,167,116,0.18), transparent 55%), radial-gradient(circle at 20% 80%, rgba(60,120,181,0.14), transparent 55%)",
          }}
        />
        <div className="relative z-10 max-w-2xl">
          <div
            className="font-mont text-[11px] tracking-[0.45em] uppercase mb-3"
            style={{ color: "var(--kd9-success)" }}
          >
            · Acompañamiento humano ·
          </div>
          <h1 className="font-cinzel kd9-gold-text text-3xl md:text-5xl tracking-[0.04em] leading-tight mb-3">
            ¿Necesitas hablar con alguien?
          </h1>
          <p
            className="font-cormorant text-xl mb-7"
            style={{ color: "var(--kd9-text)" }}
          >
            Detrás de cada diagnóstico hay una persona real. Elige cómo quieres ser acompañado hoy.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COMPANION.map((c, i) => (
              <button
                key={c.title}
                onClick={() => navigate(c.to)}
                className={`group flex items-center gap-4 p-4 rounded-xl border transition-all hover:-translate-y-0.5 kd9-fade-up kd9-delay-${i + 1}`}
                style={{
                  borderColor: `${c.color}55`,
                  background: `${c.color}10`,
                }}
                data-testid={`user-companion-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                  style={{ background: `${c.color}33`, color: c.color }}
                >
                  <c.icon size={20} />
                </div>
                <div className="text-left flex-1">
                  <div className="font-mont text-sm font-semibold">{c.title}</div>
                  <div
                    className="font-mont text-[11px]"
                    style={{ color: "var(--kd9-text-soft)" }}
                  >
                    {c.desc}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK ACCESS */}
      <h2 className="font-cinzel kd9-gold-text text-xl tracking-[0.1em] mb-5">
        ACCESOS RÁPIDOS
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {QUICK_ACCESS.map((q, i) => (
          <Link
            key={q.title}
            to={q.to}
            className={`kd9-card p-5 group kd9-fade-up kd9-delay-${i + 1}`}
            data-testid={`user-quick-${i}`}
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110"
              style={{ background: `${q.color}22`, color: q.color, border: `1px solid ${q.color}55` }}
            >
              <q.icon size={18} />
            </div>
            <div className="font-mont text-sm font-semibold">{q.title}</div>
          </Link>
        ))}
      </div>

      {/* Soporte KD9 destacado */}
      <div
        className="kd9-card p-6 flex flex-col md:flex-row items-center gap-5"
        style={{ background: "linear-gradient(135deg, rgba(31,167,116,0.12), transparent)" }}
      >
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "rgba(31,167,116,0.25)", border: "1px solid rgba(31,167,116,0.5)" }}
        >
          <Phone size={20} style={{ color: "#1FA774" }} />
        </div>
        <div className="flex-1 text-center md:text-left">
          <div className="font-cinzel kd9-gold-text text-lg tracking-wider mb-1">
            SOPORTE KD9
          </div>
          <div className="font-cormorant text-base" style={{ color: "var(--kd9-text-soft)" }}>
            ¿Problemas con tu cuenta, pagos o navegación? El equipo de soporte está disponible.
          </div>
        </div>
        <Link
          to="/app/soporte"
          className="kd9-btn-primary px-5 py-3 rounded-lg text-[11px]"
          data-testid="user-dashboard-soporte-btn"
        >
          Abrir Soporte
        </Link>
      </div>
    </div>
  );
};

export default UserDashboard;
